import React, { useEffect, useState, Suspense, lazy, memo, createElement, Component } from 'react';
// Lazy load modal pages
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions').then(m => ({
  default: m.TermsAndConditions
})));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({
  default: m.PrivacyPolicy
})));
// Declare fbq for TypeScript
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
// ===== STATIC DATA =====
const PAIN_POINTS = [{
  icon: '🧠',
  title: 'La Neblina Mental Constante',
  text: 'Te cuesta concentrarte más de 20 minutos. Olvidas cosas simples. Sientes que tu cerebro funciona al 40% de su capacidad. Y el café ya no ayuda.'
}, {
  icon: '😴',
  title: 'Despiertas Agotada',
  text: 'Duermes 8 horas y amaneces destrozada. Tu cuerpo no descansa porque por dentro hay un incendio activo que consume toda tu energía de recuperación.'
}, {
  icon: '👖',
  title: 'El Vientre que Crece Después de Comer',
  text: 'Comes "sano" pero a las 6 PM tu abdomen parece un globo. No es lo que comes, es lo que tu flora intestinal destruida no puede procesar.'
}, {
  icon: '😤',
  title: 'Los Antojos que No Puedes Controlar',
  text: 'No es falta de voluntad. Son bacterias oportunistas en tu intestino enviando señales falsas a tu cerebro para que les alimentes con azúcar.'
}];
const MECHANISM_STEPS = [{
  icon: '🦠',
  title: 'EL DETONANTE',
  text: 'Azúcares ocultos, aceites procesados y aditivos en productos "fit" destruyen tu barrera intestinal silenciosamente.'
}, {
  icon: '🔥',
  title: 'EL INCENDIO',
  text: 'Las bacterias oportunistas colonizan tu intestino y generan inflamación sistémica crónica que afecta cerebro, metabolismo y energía.'
}, {
  icon: '💊',
  title: 'EL ERROR',
  text: 'Tratamos los síntomas (café, ibuprofeno, dietas) en lugar de apagar el incendio en la raíz. El ciclo nunca termina.'
}];
const TESTIMONIALS = [{
  name: 'Lucía M. — Madrid',
  message: 'Día 4 y la neblina mental desapareció por completo. Llevo años sin sentirme tan lúcida en el trabajo. ¿Cómo no me enteré antes de esto? 🤯',
  time: '10:42 AM · Ayer'
}, {
  name: 'Carmen R. — Sevilla',
  message: 'El pantalón que no me cerraba desde hace 2 años... me lo puse hoy sin esfuerzo. Día 9. No me lo creo. El vientre por fin normal después de comer 🙌',
  time: '16:15 PM · Hace 2 días'
}, {
  name: 'Ana C. — Barcelona',
  message: 'Empecé con mucho escepticismo. Pero en el día 6 me di cuenta de que llevaba días sin necesitar la siesta de las 3. Esto es diferente a todo lo que había probado antes. Increíble.',
  time: '20:30 PM · Hace 3 días'
}];
const STATS = [{
  number: '1.200+',
  label: 'Personas'
}, {
  number: '4.8/5',
  label: 'Valoración Media'
}, {
  number: '97%',
  label: 'Repetidores'
}];
const OFFER_ITEMS = [{
  emoji: '📘',
  name: 'El Reinicio Biológico',
  description: 'El protocolo técnico de 14 días. Tu manual de batalla completo.',
  value: '97€'
}, {
  emoji: '🗺️',
  name: 'BONO 1 — Hoja de Ruta',
  description: 'Tu guía paso a paso, día a día. Cero dudas. Cero improvisación.',
  value: '27€'
}, {
  emoji: '🍳',
  name: 'BONO 2 — Recetario Express',
  description: 'Comidas de menos de 10 minutos que alimentan tu biología, no la inflamación.',
  value: '47€'
}, {
  emoji: '🔍',
  name: 'BONO 3 — Guía de Saboteadores',
  description: 'Todos los ingredientes ocultos en tu despensa que están destruyendo tu esfuerzo en el supermercado.',
  value: '17€'
}, {
  emoji: '🛒',
  name: 'BONO 4 — Lista de Compras Inteligente',
  description: 'El inventario exacto para empezar desde hoy. Nada que inventarse.',
  value: '17€'
}];
const FAQS = [{
  q: '¿Funciona aunque lleve años con estos síntomas?',
  a: 'Sí. El protocolo actual a nivel del microbioma, que se regenera independientemente del tiempo que lleves con inflamación. De hecho, muchos usuarios con síntomas crónicos reportan los cambios más rápidos y dramáticos.'
}, {
  q: '¿Tengo que hacer dieta estricta o pasar hambre?',
  a: 'Absolutamente no. El Reinicio Biológico no es una dieta restrictiva. Es un protocolo de alimentación inteligente que saciará más y te dará más energía — no menos.'
}, {
  q: '¿En cuánto tiempo veré resultados reales?',
  a: 'La mayoría de usuarios reporta los primeros cambios entre el día 3 y el día 7. La claridad mental suele ser lo primero. El vientre y la energía, antes del día 14.'
}, {
  q: '¿Es apto para hombres y mujeres?',
  a: '100%. La inflamación sistémica no discrimina. El protocolo está diseñado para funcionar en cualquier perfil biológico adulto.'
}, {
  q: '¿Qué pasa si no funciona para mí?',
  a: 'Tienes 7 días completos para probarlo. Si no sientes la diferencia, te devolvemos el 100% del dinero. Sin preguntas. Sin formularios complicados.'
}, {
  q: '¿Cómo accedo después del pago?',
  a: 'Inmediatamente. Recibirás un email con acceso a tu área de descarga en menos de 2 minutos. Compatible con móvil, tablet y ordenador.'
}];
const HERO_BADGES = ['🔒 Pago Seguro', '⚡ Acceso Inmediato', '↩️ Garantía 7 Días', '🇪🇸 Creado en España'];
// Isolated countdown timer — delays start 4s to not block hero paint
const CountdownTimer = memo(function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState({
    minutes: 14,
    seconds: 9
  });
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const delay = setTimeout(() => setStarted(true), 4000);
    return () => clearTimeout(delay);
  }, []);
  useEffect(() => {
    if (!started) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          minutes: prev.minutes - 1,
          seconds: 59
        };
        return {
          minutes: 14,
          seconds: 9
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [started]);
  return <span className="timer-badge" data-id="element-0">
      <span className="timer-digits" data-id="element-1">
        {String(timeLeft.minutes).padStart(2, '0')}:
        {String(timeLeft.seconds).padStart(2, '0')}
      </span>
    </span>;
});
// Lightweight Modal Component
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return <>
      <div className="modal-backdrop fade-in" onClick={onClose} data-id="element-2" />
      <div className="modal" data-id="element-3">
        <div className="modal-content modal-in" data-id="element-4">
          <div className="modal-header" data-id="element-5">
            <h2 className="font-serif text-white text-xl md:text-2xl" data-id="element-6">
              {title}
            </h2>
            <button onClick={onClose} className="modal-close-btn" aria-label="Cerrar" data-id="element-7">
              ×
            </button>
          </div>
          <div className="modal-body" data-id="element-8">{children}</div>
        </div>
      </div>
    </>;
}
function Home() {
  const [openModal, setOpenModal] = useState<'terms' | 'privacy' | null>(null);
  const [showBelowFold, setShowBelowFold] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  // Defer sticky bar: appear after 5s so hero paints fast
  useEffect(() => {
    const timer = setTimeout(() => setShowStickyBar(true), 5000);
    return () => clearTimeout(timer);
  }, []);
  // Defer below-fold: use requestIdleCallback for better scheduling
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(() => setShowBelowFold(true));
      return () => cancelIdleCallback(id);
    } else {
      const id = requestAnimationFrame(() => setShowBelowFold(true));
      return () => cancelAnimationFrame(id);
    }
  }, []);
  // Meta Pixel - Load immediately on page load
  useEffect(() => {
    const setupCriticalResources = () => {
      // Preconnect to CDN (faster than dns-prefetch — establishes full connection)
      const preconnect = document.createElement('link');
      preconnect.rel = 'preconnect';
      preconnect.href = 'https://cdn.magicpatterns.com';
      preconnect.crossOrigin = 'anonymous';
      document.head.appendChild(preconnect);
      // Preconnect to payment domain — warm connection before user clicks CTA
      const hotmartPreconnect = document.createElement('link');
      hotmartPreconnect.rel = 'preconnect';
      hotmartPreconnect.href = 'https://pay.hotmart.com';
      document.head.appendChild(hotmartPreconnect);
      // DNS prefetch for Facebook pixel domain
      const fbDns = document.createElement('link');
      fbDns.rel = 'dns-prefetch';
      fbDns.href = 'https://connect.facebook.net';
      document.head.appendChild(fbDns);
      // Preload hero image — WebP format (LCP optimization)
      const heroPreload = document.createElement('link');
      heroPreload.rel = 'preload';
      heroPreload.as = 'image';
      heroPreload.type = 'image/webp';
      heroPreload.href = 'https://cdn.magicpatterns.com/uploads/fPHA9Qc9ZQ8HbPsQE6fMKT/LIBRO_EL_REINICIO_BIOLOGICO_2B_BONOS_(1)_(1).webp';
      heroPreload.fetchPriority = 'high';
      document.head.appendChild(heroPreload);
    };
    !function (f: any, b: any, e: any, v: any, n: any, t: any, s: any) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      if (s && s.parentNode) {
        s.parentNode.insertBefore(t, s);
      } else {
        b.head.appendChild(t);
      }
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js', null, null, null);
    window.fbq('init', '1377433624081067');
    window.fbq('track', 'PageView');
    setupCriticalResources();
  }, []);
  const handlePaymentClick = () => {
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'El Reinicio Biológico',
        content_ids: ['reinicio-biologico'],
        content_type: 'product',
        value: 26.0,
        currency: 'EUR'
      });
    }
    window.open('https://pay.hotmart.com/W104988940M?bid=1774468896416', '_blank');
  };
  return <div className="page-root" data-id="element-9">
      {/* STICKY URGENCY BAR — deferred 5s for faster hero paint */}
      {showStickyBar && <div className="sticky-bar sticky-bar-animate" data-id="element-10">
          <div className="container sticky-bar-inner" data-id="element-11">
            <div className="sticky-bar-top" data-id="element-12">
              <span className="uppercase text-[12px] font-[700]" data-id="element-13">
                OFERTA POR LANZAMIENTO - SOLO QUEDAN 19 UNIDADES
              </span>
              <CountdownTimer data-id="element-14" />
            </div>
            <button onClick={handlePaymentClick} className="btn btn-primary w-full md:w-auto whitespace-nowrap text-[16px]" data-id="element-15">
              CONSEGUIR MI REINICIO POR 26€ →
            </button>
          </div>
        </div>}

      {/* HERO — Mobile-optimized: CTA above fold */}
      <section className="hero" data-id="element-16">
        <div className="container hero-container" data-id="element-17">
          <p className="text-gold text-xs uppercase font-semibold mb-3 tracking-wide" data-id="element-18">
            EL PROTOCOLO QUE NADIE TE HABÍA CONTADO
          </p>

          <h1 className="font-serif text-white text-2xl md:text-5xl leading-tight mb-4" data-id="element-19">
            ¿Por qué te sientes agotada, hinchada y sin foco?
          </h1>

          <h2 className="text-gold text-lg md:text-2xl mb-6 font-semibold pt-[10px] pb-[10px]" data-id="element-20">
            No es tu edad. Es Inflamación Silenciosa destruyendo tu biología.
          </h2>

          <div className="mb-6" data-id="element-21">
            <img src="https://cdn.magicpatterns.com/uploads/fPHA9Qc9ZQ8HbPsQE6fMKT/LIBRO_EL_REINICIO_BIOLOGICO_2B_BONOS_(1)_(1).webp" alt="El Reinicio Biológico" width="220" height="220" loading="eager" fetchPriority="high" decoding="async" className="hero-img" data-id="element-22" />
          </div>

          <div className="mb-4" data-id="element-23">
            <p className="text-base mb-1 text-red" data-id="element-24">
              <span className="price-strike" data-id="element-25">205€</span>
            </p>
            <p className="text-gold text-4xl md:text-6xl font-serif font-bold mb-1" data-id="element-26">
              HOY: 26€
            </p>
            <p className="text-xs text-muted" data-id="element-27">
              (IVA incluido · Acceso inmediato)
            </p>
          </div>

          <button onClick={handlePaymentClick} className="btn btn-primary text-lg md:text-xl mb-4 btn-hero" data-id="element-28">
            SÍ, QUIERO MI REINICIO — 26€
          </button>

          <p className="text-xs mb-6 text-muted" data-id="element-29">
            🔒 Pago seguro · Acceso instantáneo · Garantía 7 días
          </p>

          <p className="text-white text-sm md:text-base leading-relaxed mb-6 mx-auto hero-text" data-id="element-30">
            Tu cuerpo está librando una guerra invisible. Microorganismos
            oportunistas secuestran tu energía, nublan tu cerebro y deforman tu
            vientre. El Reinicio Biológico apaga ese incendio en 14 días. Sin
            dietas extremas. Sin pastillas.
          </p>

          <div className="hide-mobile flex-wrap justify-center gap-3" data-id="element-31">
            {HERO_BADGES.map((badge, i) => <span key={i} className="badge-pill text-gold text-xs font-semibold whitespace-nowrap" data-id="element-32">
                {badge}
              </span>)}
          </div>
        </div>
      </section>

      {showBelowFold && <>
          {/* PAIN AMPLIFICATION */}
          <section className="bg-dark section-padding" data-id="element-33">
            <div className="container max-w-6xl" data-id="element-34">
              <p className="text-gold text-xs md:text-sm uppercase text-center mb-6 font-semibold tracking-wide" data-id="element-35">
                ¿TE IDENTIFICAS CON ESTO?
              </p>

              <h2 className="font-serif text-white text-2xl md:text-4xl text-center mb-4 mx-auto leading-tight max-w-3xl" data-id="element-36">
                "Pensé que estar cansada, hinchada y sin energía... era
                simplemente parte de hacerse mayor."
              </h2>

              <p className="text-gold italic text-lg md:text-xl text-center mb-12" data-id="element-37">
                — Lo que nadie te dijo es que hay una causa real. Y tiene
                solución.
              </p>

              <div className="grid grid-1 md:grid-2 mb-12 gap-grid" data-id="element-38">
                {PAIN_POINTS.map((card, i) => <div key={i} className="pain-card" data-id="element-39">
                    <h3 className="text-gold font-bold text-lg mb-3" data-id="element-40">
                      {card.icon} {card.title}
                    </h3>
                    <p className="text-white text-sm md:text-base leading-relaxed" data-id="element-41">
                      {card.text}
                    </p>
                  </div>)}
              </div>

              <p className="text-white text-center italic text-base md:text-lg mx-auto leading-relaxed max-w-2xl" data-id="element-42">
                Si reconoces 2 o más de estos síntomas, tu cuerpo lleva meses
                enviándote señales de auxilio. Cada día que pasa sin actuar, el
                incendio interno se hace más difícil de apagar.
              </p>
            </div>
          </section>

          {/* THE MECHANISM */}
          <section className="bg-light section-padding" data-id="element-43">
            <div className="container max-w-6xl" data-id="element-44">
              <p className="text-xs md:text-sm uppercase text-center mb-6 font-semibold tracking-wide text-emerald" data-id="element-45">
                LA CAUSA RAÍZ QUE NADIE TE EXPLICA
              </p>

              <h2 className="font-serif text-2xl md:text-4xl text-center leading-tight text-dark mb-16" data-id="element-46">
                El Problema No Es Tu Fuerza de Voluntad.
                <br data-id="element-47" />
                Es Tu Microbioma Destruido.
              </h2>

              <div className="grid grid-1 md:grid-3 mb-16 gap-section" data-id="element-48">
                {MECHANISM_STEPS.map((step, i) => <div key={i} className="text-center" data-id="element-49">
                    <div className="icon-xl" data-id="element-50">{step.icon}</div>
                    <h3 className="font-bold text-lg md:text-xl mb-3 uppercase text-emerald tracking-tight" data-id="element-51">
                      {step.title}
                    </h3>
                    <p className="text-sm md:text-base leading-relaxed text-gray-700" data-id="element-52">
                      {step.text}
                    </p>
                  </div>)}
              </div>

              <div className="bg-emerald rounded-3xl p-8 md:p-12 text-center" data-id="element-53">
                <h3 className="font-serif text-gold text-2xl md:text-3xl mb-6 font-[600]" data-id="element-54">
                  El Reinicio Biológico interrumpe ese ciclo en 14 días.
                </h3>
                <p className="text-white text-base md:text-lg leading-relaxed mx-auto max-w-2xl" data-id="element-55">
                  No es una dieta. No es un suplemento. Es un protocolo técnico
                  de reprogramación del microbioma que actúa directamente sobre
                  la causa raíz — no sobre los síntomas.
                </p>
              </div>
            </div>
          </section>

          {/* SOCIAL PROOF */}
          <section className="bg-white section-padding" data-id="element-56">
            <div className="container max-w-6xl" data-id="element-57">
              <p className="text-gold text-xs md:text-sm uppercase text-center mb-6 font-semibold tracking-wide" data-id="element-58">
                RESULTADOS REALES DE PERSONAS EN ESPAÑA
              </p>

              <h2 className="font-serif md:text-4xl text-center mb-4 text-dark text-[20px] font-[600]" data-id="element-59">
                Más de 1.200 personas ya iniciaron su Reinicio Biológico
              </h2>

              <p className="text-center mb-12 text-gray-600" data-id="element-60">
                Estos son algunos de sus mensajes:
              </p>

              <div className="grid grid-1 md:grid-2 mb-12 gap-cards" data-id="element-61">
                {TESTIMONIALS.map((t, i) => <div key={i} className="testimonial" data-id="element-62">
                    <div className="testimonial-header flex items-center gap-3" data-id="element-63">
                      <div className="avatar" data-id="element-64">{t.name[0]}</div>
                      <p className="text-white font-semibold text-sm flex items-center gap-2" data-id="element-65">
                        {t.name}
                        <span className="verified-badge" data-id="element-66">✓</span>
                      </p>
                    </div>
                    <div className="p-6" data-id="element-67">
                      <div className="testimonial-bubble mb-3" data-id="element-68">
                        <p className="text-sm md:text-base leading-relaxed text-dark" data-id="element-69">
                          {t.message}
                        </p>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-70">
                        <p className="text-xs text-gray-500" data-id="element-71">{t.time}</p>
                        <div className="flex gap-1" data-id="element-72">
                          <span className="text-gold" data-id="element-73">⭐⭐⭐⭐⭐</span>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="grid grid-1 md:grid-3 text-center stats-border" data-id="element-74">
                {STATS.map((stat, i) => <div key={i} data-id="element-75">
                    <p className="text-gold text-3xl md:text-4xl font-serif font-bold mb-1" data-id="element-76">
                      {stat.number}
                    </p>
                    <p className="text-sm md:text-base text-gray-600" data-id="element-77">
                      {stat.label}
                    </p>
                  </div>)}
              </div>
            </div>
          </section>

          {/* OFFER STACK */}
          <section className="bg-light section-padding" data-id="element-78">
            <div className="container max-w-5xl" data-id="element-79">
              <p className="text-gold text-xs md:text-sm uppercase text-center mb-6 font-semibold tracking-wide" data-id="element-80">
                TODO LO QUE RECIBES HOY
              </p>

              <h2 className="font-serif text-2xl md:text-4xl text-center mb-12 text-dark" data-id="element-81">
                Acceso Completo y Vitalicio al Sistema
              </h2>

              <div className="grid grid-1 md:grid-2 mb-12 gap-grid" data-id="element-82">
                {OFFER_ITEMS.map((item, i) => <div key={i} className="card card-border flex gap-4" data-id="element-83">
                    <div className="offer-thumb flex items-center justify-center" style={{
                background: 'var(--emerald-light)',
                fontSize: '2rem'
              }} data-id="element-84">
                      {item.emoji}
                    </div>
                    <div className="flex-1" data-id="element-85">
                      <h3 className="font-bold text-base md:text-lg mb-2 text-dark" data-id="element-86">
                        {item.name}
                      </h3>
                      <p className="text-sm mb-3 leading-relaxed text-gray-600" data-id="element-87">
                        {item.description}
                      </p>
                      <span className="value-badge text-gold text-xs font-semibold" data-id="element-88">
                        Valor: {item.value}
                      </span>
                    </div>
                  </div>)}
              </div>

              <div className="bg-dark rounded-3xl p-8 md:p-12 text-center" data-id="element-89">
                <p className="text-sm uppercase mb-2 text-muted tracking-tight" data-id="element-90">
                  VALOR TOTAL DEL SISTEMA:
                </p>
                <p className="text-2xl md:text-3xl mb-4 text-red" data-id="element-91">
                  <span className="price-strike" data-id="element-92">205€</span>
                </p>
                <p className="text-gold text-base uppercase mb-2 tracking-tight" data-id="element-93">
                  TU INVERSIÓN HOY:
                </p>
                <p className="text-white text-5xl md:text-7xl font-serif font-bold mb-6" data-id="element-94">
                  26€
                </p>
                <p className="text-sm mb-8 text-muted" data-id="element-95">(IVA incluido)</p>

                <button onClick={handlePaymentClick} className="btn btn-primary w-full text-lg md:text-xl mb-4 btn-lg" data-id="element-96">
                  QUIERO ACCESO COMPLETO — 26€
                </button>

                <p className="text-sm text-muted" data-id="element-97">
                  ⚡ Acceso inmediato tras el pago
                </p>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="bg-white section-padding" data-id="element-98">
            <div className="container max-w-3xl" data-id="element-99">
              <h2 className="font-serif text-2xl md:text-4xl text-center mb-12 text-dark" data-id="element-100">
                Tus Preguntas. Respondidas con Honestidad.
              </h2>

              <div className="faq-list" data-id="element-101">
                {FAQS.map((faq, i) => <details key={i} data-id="element-102">
                    <summary data-id="element-103">
                      <span className="text-base md:text-lg faq-question" data-id="element-104">
                        {faq.q}
                      </span>
                      <span className="icon" data-id="element-105">+</span>
                    </summary>
                    <div className="faq-answer" data-id="element-106">
                      <p className="text-sm md:text-base leading-relaxed text-gray-600" data-id="element-107">
                        {faq.a}
                      </p>
                    </div>
                  </details>)}
              </div>
            </div>
          </section>

          {/* GUARANTEE */}
          <section className="bg-dark section-padding" data-id="element-108">
            <div className="container text-center max-w-2xl" data-id="element-109">
              <div className="icon-xxl" data-id="element-110">🛡️</div>

              <h2 className="font-serif text-gold text-2xl md:text-4xl mb-8" data-id="element-111">
                La Garantía Zeklo: Sin Riesgo. Sin Trampa.
              </h2>

              <p className="text-white text-base md:text-lg leading-relaxed mb-8" data-id="element-112">
                Tienes 7 días para probarlo todo. Aplica el protocolo. Siente la
                diferencia.
                <br data-id="element-113" />
                <br data-id="element-114" />
                Si después de 7 días no notas ningún cambio, te devolvemos el
                100% de tu dinero. Sin preguntas. Sin formularios imposibles.
                Sin esperas. Solo un email y listo.
              </p>

              <p className="text-gold italic font-semibold text-xl" data-id="element-115">
                El riesgo es nuestro. El resultado es tuyo.
              </p>
            </div>
          </section>

          {/* FINAL CTA */}
          <section className="section-padding final-cta" data-id="element-116">
            <div className="container text-center max-w-3xl" data-id="element-117">
              <h2 className="font-serif text-white text-2xl md:text-4xl mb-8" data-id="element-118">
                ¿Cuánto más vas a esperar?
              </h2>

              <p className="text-white text-base md:text-lg leading-relaxed mb-8 mx-auto max-w-2xl" data-id="element-119">
                Cada día que pasa con inflamación activa, la neblina crece. El
                vientre empeora. La energía se agota más.
              </p>

              <div className="final-cta-box rounded-3xl p-8 md:p-10 mb-8" data-id="element-120">
                <p className="text-xl md:text-2xl mb-3 text-red" data-id="element-121">
                  <span className="price-strike" data-id="element-122">205€</span>
                </p>
                <p className="text-gold text-4xl md:text-6xl font-serif font-bold mb-3" data-id="element-123">
                  HORA DE REINICIAR: 26€
                </p>
                <p className="text-sm mb-8 text-light-muted" data-id="element-124">
                  (IVA incluido · Acceso inmediato)
                </p>

                <button onClick={handlePaymentClick} className="btn btn-primary w-full text-lg md:text-2xl mb-6 btn-xl" data-id="element-125">
                  ⚡ SÍ, QUIERO MI REINICIO BIOLÓGICO — 26€
                </button>

                <div className="trust-signals" data-id="element-126">
                  <p data-id="element-127">🔒 Pago 100% seguro</p>
                  <p data-id="element-128">📱 Acceso instantáneo</p>
                  <p data-id="element-129">↩️ Garantía 7 días sin preguntas</p>
                </div>
              </div>
            </div>
          </section>

          {/* FOOTER */}
          <footer className="site-footer" data-id="element-130">
            <div className="container text-center max-w-4xl" data-id="element-131">
              <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-6 footer-links" data-id="element-132">
                <button onClick={() => setOpenModal('terms')} className="footer-link" data-id="element-133">
                  Términos y Condiciones
                </button>
                <span className="hidden md:inline" data-id="element-134">·</span>
                <button onClick={() => setOpenModal('privacy')} className="footer-link" data-id="element-135">
                  Política de Privacidad
                </button>
                <span className="hidden md:inline" data-id="element-136">·</span>
                <a href="mailto:zeklo.club@gmail.com" className="footer-link" data-id="element-137">
                  Contacto
                </a>
              </div>

              <p className="text-xs md:text-sm mb-4 text-gray-500" data-id="element-138">
                © 2026 Zeklo Club. Todos los derechos reservados.
              </p>

              <p className="text-xs italic mx-auto leading-relaxed text-gray-600 max-w-2xl" data-id="element-139">
                Este producto no sustituye el consejo médico profesional. Los
                resultados individuales pueden variar según la persona.
              </p>
            </div>
          </footer>

          {/* Modals */}
          <Modal isOpen={openModal === 'terms'} onClose={() => setOpenModal(null)} title="Términos y Condiciones" data-id="element-140">
            <Suspense fallback={<div className="text-center py-12" data-id="element-142">Cargando...</div>} data-id="element-141">
              <TermsAndConditions data-id="element-143" />
            </Suspense>
          </Modal>

          <Modal isOpen={openModal === 'privacy'} onClose={() => setOpenModal(null)} title="Política de Privacidad" data-id="element-144">
            <Suspense fallback={<div className="text-center py-12" data-id="element-146">Cargando...</div>} data-id="element-145">
              <PrivacyPolicy data-id="element-147" />
            </Suspense>
          </Modal>
        </>}
    </div>;
}
export function App() {
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = 'a[href*="magicpatterns"], [class*="magic-patterns"], #magic-patterns-badge { display: none !important; }';
    document.head.appendChild(style);
    const showTimer = setTimeout(() => {
      style.remove();
    }, 60000);
    return () => {
      clearTimeout(showTimer);
      style.remove();
    };
  }, []);
  return <Home data-id="element-148" />;
}